import React, { useMemo, useState } from 'react';
import { CheckIcon, PencilIcon, RotateCcwIcon } from 'lucide-react';
import { LoanRecord } from '../../types/highlights';
import { useHighlightsPanel } from '../../hooks/useHighlightsPanel';
import { fieldCatalog } from '../../data/fieldCatalog';
import { textScaleStyles } from '../../utils/layout';
import { evaluateField } from '../../utils/rules';
import { HighlightChip } from './HighlightChip';
import { AddFieldControl } from './AddFieldControl';
import { HighlightSettings } from './HighlightSettings';
import { RulesEditor } from './RulesEditor';
interface HighlightsPanelProps {
  loan: LoanRecord;
  rulesEnabled: boolean;
  /** Whether the current user is an admin who may configure the panel. */
  canConfigure: boolean;
  defaultEditing?: boolean;
}
export function HighlightsPanel({
  loan,
  rulesEnabled,
  canConfigure,
  defaultEditing = false
}: HighlightsPanelProps) {
  const [isEditingState, setIsEditing] = useState(defaultEditing);
  const isEditing = canConfigure && isEditingState;
  const {
    fields,
    byId,
    availableFields,
    toneColors,
    rules,
    layout,
    addField,
    removeField,
    setColorOverride,
    clearAllOverrides,
    moveField,
    setFieldSpan,
    setFieldMaxChars,
    setLayout,
    normalizeFieldSizes,
    setToneColor,
    addRule,
    updateRule,
    removeRule,
    resetFields,
    resetHighlightSettings
  } = useHighlightsPanel();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesFocusFieldId, setRulesFocusFieldId] = useState<string | null>(null);
  const activeRules = rulesEnabled ? rules : [];
  const matches = useMemo(() => {
    const map = new Map<string, ReturnType<typeof evaluateField>>();
    fields.forEach(field => {
      map.set(field.fieldId, evaluateField(field.fieldId, activeRules, loan, byId));
    });
    return map;
  }, [fields, activeRules, loan, byId]);
  const attentionCount = useMemo(() => fields.filter(field => {
    if (field.colorOverride === 'critical' || field.colorOverride === 'warning') return true;
    if (field.colorOverride) return false;
    const match = matches.get(field.fieldId);
    return match?.tone === 'critical' || match?.tone === 'warning';
  }).length, [fields, matches]);
  const openRules = (fieldId: string | null) => {
    setRulesFocusFieldId(fieldId);
    setRulesOpen(true);
  };
  const handleDrop = (index: number) => {
    if (dragFrom !== null) moveField(dragFrom, index);
    setDragFrom(null);
    setDragOver(null);
  };
  return <section aria-label="Loan highlights" className={['w-full rounded-md border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition', isEditing ? 'border-[#0b5cab] ring-2 ring-[#0b5cab]/15' : 'border-[#d6e0f0]'].join(' ')} data-id="element-104" data-mp="2qfczuknnlzqb">
      <header className={['flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-4 py-2.5', isEditing ? 'border-[#cfe0f5] bg-[#f3f8ff]' : 'border-[#e6ecf7]'].join(' ')} data-id="element-105" data-mp="22q25bkwwd1km">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1" data-id="element-106" data-mp="1m7e7syvp54e8">
          <h2 className="text-sm font-bold text-slate-900" data-id="element-107" data-mp="1m9inee2yjruc">Highlights</h2>
          {isEditing ? <span className="inline-flex items-center gap-1 rounded bg-[#0b5cab] px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-white" data-id="element-108" data-mp="1hafictmz9lmd">
              <PencilIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-109" data-mp="17hgsz9jnpr1p" />
              Editing
            </span> : null}
          <span className="text-[12px] text-slate-500" data-id="element-110" data-mp="12joprjdkaii7">
            {fields.length} {fields.length === 1 ? 'field' : 'fields'}
          </span>
          {attentionCount > 0 ? <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-id="element-111" data-mp="thfgewu14v6e">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-amber-600" data-id="element-112" data-mp="2oldhcbyhur86" />
              {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
            </span> : <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800" data-id="element-113" data-mp="2ewluujc7cz4n">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-600" data-id="element-114" data-mp="25vf99vup4y4j" />
              No flags
            </span>}
        </div>

        {isEditing ? <div className="flex items-center gap-2" data-id="element-115" data-mp="29uwh2vxli7cv">
          <AddFieldControl availableFields={availableFields} loan={loan} variant="button" onAdd={addField} data-id="element-116" data-mp="1vy12knykrnpf" />
          <HighlightSettings fields={fields} byId={byId} loan={loan} toneColors={toneColors} rules={rules} layout={layout} onSetToneColor={setToneColor} onClearAllOverrides={clearAllOverrides} onSetLayout={setLayout} onNormalizeFieldSizes={normalizeFieldSizes} onManageRules={openRules} onResetSettings={resetHighlightSettings} data-id="element-117" data-mp="3qb92uv5bkrig" />
          <button type="button" onClick={resetFields} title="Reset layout" className="flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-118" data-mp="1temn77554upn">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-119" data-mp="2caw92gmaapk7" />
            Reset
          </button>
          <button type="button" onClick={() => setIsEditing(false)} className="flex items-center gap-1.5 rounded-md bg-[#0b5cab] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-[#084a8c] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab] focus-visible:ring-offset-2" data-id="element-120" data-mp="27ahyorx7n3j0">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-121" data-mp="16niw0pps3iy0" />
            Done
          </button>
        </div> : canConfigure ? <button type="button" onClick={() => setIsEditing(true)} className="flex items-center gap-1.5 rounded-md border border-[#cfe0f5] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-122" data-mp="1bpgkpzgy75yc">
            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-123" data-mp="1udlkywyec8se" />
            Edit highlights
          </button> : null}
      </header>

      <div className="grid items-center gap-2 px-4 pb-4 pt-3.5" style={{
      gridTemplateColumns: `repeat(auto-fill, minmax(${layout.columnWidth}px, 1fr))`
    }} data-id="element-124" data-mp="1zf2fmaqaez0j">
        {fields.map((field, index) => {
        const definition = byId.get(field.fieldId);
        if (!definition) return null;
        return <HighlightChip key={field.fieldId} definition={definition} field={field} loan={loan} index={index} total={fields.length} toneColors={toneColors} layout={layout} match={matches.get(field.fieldId) ?? null} ruleCount={rules.filter(rule => rule.targetFieldId === field.fieldId).length} editable={isEditing} isDragTarget={dragOver === index && dragFrom !== null && dragFrom !== index} onDragStartChip={setDragFrom} onDragOverChip={setDragOver} onDropChip={handleDrop} onDragEndChip={() => {
          setDragFrom(null);
          setDragOver(null);
        }} onMove={moveField} onRemove={removeField} onSetColorOverride={setColorOverride} onSetSpan={setFieldSpan} onSetMaxChars={setFieldMaxChars} onManageRules={openRules} data-id="element-125" data-mp="3ot23sm6kmy5x" data-mpi={index} />;
      })}

        {isEditing ? <AddFieldControl availableFields={availableFields} loan={loan} variant="chip" heightClass={textScaleStyles[layout.textScale].height} onAdd={addField} data-id="element-126" data-mp="3pwhftycjftca" /> : null}
      </div>

      <RulesEditor open={rulesOpen && isEditing} focusFieldId={rulesFocusFieldId} fields={fields} catalog={fieldCatalog} byId={byId} rules={rules} loan={loan} toneColors={toneColors} onAddRule={addRule} onUpdateRule={updateRule} onRemoveRule={removeRule} onClose={() => setRulesOpen(false)} data-id="element-127" data-mp="qgkqpsvzdutz" />
    </section>;
}